/* eslint-disable no-case-declarations */
import { setCookie } from '@cian/utils/lib/browser/cookie';
import React, { useCallback, useEffect, useState, FC } from 'react';
import { EXPANSION_REGION_DISABLE_COOKIE } from '../../constants/region';

import { EMetaStatus, IMetaState, IGeocodeState, EGeocodeStatus } from '../../types/filters';
import { getCookie } from '../../utils/cookie_helper';
import { FiltersSearchButton } from '../FiltersSearchButton';
import styles from './FiltersSearchButtons.css';

/**
 * @interface
 */
export interface IFiltersSearchButtonsProps {
  meta: IMetaState;
  geocode: IGeocodeState;
  offerCount?: string;
  getMeta(): void;
  persistFilters(): void;
  disableRegionExpansion?: boolean;
}

export enum EFilterSearchButtonsStatus {
  ListLoading = 'listLoading',
  MapLoading = 'mapLoading',
}

export const FiltersSearchButtons: FC<IFiltersSearchButtonsProps> = ({
  meta,
  geocode,
  offerCount,
  getMeta,
  persistFilters,
  disableRegionExpansion,
}) => {
  const { mapUrl, url } = meta;
  const [status, setStatus] = useState<EFilterSearchButtonsStatus | null>(null);

  const handleSearchButtonClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, isMap: boolean = false) => {
      setStatus(isMap ? EFilterSearchButtonsStatus.MapLoading : EFilterSearchButtonsStatus.ListLoading);

      if (geocode.status === EGeocodeStatus.Loading || meta.status !== EMetaStatus.Succeed) {
        event.preventDefault();
      }

      if (
        [EGeocodeStatus.Initial, EGeocodeStatus.Succeed].includes(geocode.status) &&
        meta.status === EMetaStatus.Failed
      ) {
        getMeta();
      }

      if (disableRegionExpansion && !getCookie(document.cookie, EXPANSION_REGION_DISABLE_COOKIE)) {
        setCookie(EXPANSION_REGION_DISABLE_COOKIE, '1');
      }
    },
    [disableRegionExpansion, geocode.status, getMeta, meta.status],
  );

  const handleListSearchButtonClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      persistFilters();
      handleSearchButtonClick(event);
    },
    [handleSearchButtonClick, persistFilters],
  );

  const handleMapSearchButtonClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      persistFilters();
      handleSearchButtonClick(event, true);
    },
    [handleSearchButtonClick, persistFilters],
  );

  useEffect(() => {
    if (status !== null) {
      if (
        [EGeocodeStatus.Initial, EGeocodeStatus.Succeed].includes(geocode.status) &&
        meta.status !== EMetaStatus.Loading
      ) {
        switch (meta.status) {
          case EMetaStatus.Succeed:
            const isMap = status === EFilterSearchButtonsStatus.MapLoading;
            const url = isMap ? meta.mapUrl : meta.url;

            window.location.href = url;
            break;

          case EMetaStatus.Failed:
            setStatus(null);
            break;
        }
      }

      if (geocode.status === EGeocodeStatus.Failed) {
        setStatus(null);
      }
    }
  }, [geocode.status, meta.mapUrl, meta.status, meta.url, status]);

  return (
    <span className={styles['container']}>
      <FiltersSearchButton
        dataMark="FiltersSearchOnMapButton"
        text="Показать на карте"
        title={offerCount}
        href={mapUrl}
        onClick={handleMapSearchButtonClick}
        isLoading={status === EFilterSearchButtonsStatus.MapLoading}
        isLight
      />
      <FiltersSearchButton
        dataMark="FiltersSearchButton"
        text="Найти"
        title={offerCount}
        href={url}
        onClick={handleListSearchButtonClick}
        isLoading={status === EFilterSearchButtonsStatus.ListLoading}
      />
    </span>
  );
};
